// GENERAL
import { useState, useEffect } from "react";
import {
	useQuery,
	useMutation,
	UseMutationResult,
	useQueryClient,
} from "@tanstack/react-query";

// API
import {
	fetchOrdersDetails,
	fetchOrderItems,
	fetchPurchaseOrderDetails,
	addPurchaseOrderInputAndCreditCardInfo,
} from "../../../api/OrdersApi";
import { getCurrentApprovalStage } from "../../../view/api/OrderViewApi";

// TYPES
import { SalesOrder, ShipToAddress, SalesOrderItem } from "../../../types";
import { ApprovalDecision } from "../../../types";

import { FormattedSalesOrderNotes } from "../../../types";
// FIELDS
import {
	SUPERUSERFIELDS_COMPASS,
	ADMINFIELDS_COMPASS,
	MANAGERFIELDS_COMPASS,
	ADMINFIELDS_COMPASSCANADA,
	SUPERUSERFIELDS_COMPASSCANADA,
	MANAGERFIELDS_COMPASSCANADA,
	ADMINFIELDS_QUAD,
	ITSHOPPERFIELDS_QUAD,
	BUYERFIELDS_QUAD,
} from "./index";

// HOOKS
import { useDataTableContext, usePageDataContext } from "../../../../../hooks";
import { useToaster } from "../../../../../hooks/useToasterContext";
import useHostNameStore from "../../../../../stores/useHostNameStore";
import useUserStore from "../../../../../stores/useUserStore";

// HELPERS
import {
	formatOrderItems,
	getChildBundleDataFromApi,
	formatSalesOrderBundlesPreLaunchCommerce,
	pairSalesOrderBundlesFromApi,
	formatSalesOrderBundles,
} from "../helpers";
import { getPrimaryItems } from "../helpers/formatSalesOrderBundlesPreLaunchCommerce";
import { getBundleWithPrimaryItemAndBundleItems } from "../helpers/formatSalesOrderBundlesPreLaunchCommerce";
import { formatSalesOrderNotes } from "../../../helpers";

export const useApprovalsModalViewModel = (): {
	orderDetails: SalesOrder;
	orderAddressDetails: ShipToAddress | null | undefined;
	setRefetchData: React.Dispatch<React.SetStateAction<boolean>>;
	refetchData: boolean;
	aclPermissions:
		| {
				[key in
					| "hasEditInCommercePermissions"
					| "hasApprovalsPermissions"]?: boolean;
		  }
		| {};
	salesOrderNotes: FormattedSalesOrderNotes | null;
	fields: Record<string, any>;
	selectedSingleItems: any;
	selectedBundles: any;
	salesOrderItems: SalesOrderItem[];
	contractorInfo?: string | null | undefined;
	showErrorMessage: boolean;
	approvalDecisions: ApprovalDecision;
	purchaseOrderDetails: any;
	showLoadingState: boolean;
	activeUuid: string | null;
	addPurchaseOrderInputAndCreditCardInfoMutation: UseMutationResult<
		any,
		Error,
		{
			salesOrderUuid: string;
			poNumber: string;
			customerUuid: string;
			dateOrder: string;
			purchaseOrderUuid: string | null;
			c_requiresCreditCard: boolean;
		},
		unknown
	>;
} => {
	const { activeUuid } = useDataTableContext();
	const { pageMeta } = usePageDataContext();
	const { hostName } = useHostNameStore();
	const { user } = useUserStore();
	const { addToaster } = useToaster();
	const queryClient = useQueryClient();

	const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
	const [refetchData, setRefetchData] = useState<boolean>(false);
	const [aclPermissions, setAclPermissions] = useState<{
		hasEditInCommercePermissions?: boolean;
		hasApprovalsPermissions?: boolean;
	}>({});
	const [isDataFormattingLoading, setIsDataFormattingLoading] =
		useState<boolean>(true);
	const [isInitialDataProcessed, setIsInitialDataProcessed] =
		useState<boolean>(false);

	const [orderAddressDetails, setOrderAddressDetails] = useState<
		ShipToAddress | null | undefined
	>();
	const [orderDetails, setOrderDetails] = useState<SalesOrder>(
		{} as SalesOrder,
	);
	const [purchaseOrderDetails, setPurchaseOrderDetails] = useState<any>(
		{} as any,
	);
	const [salesOrderItems, setSalesOrderItems] = useState<SalesOrderItem[]>(
		[],
	);
	const [salesOrderNotes, setSalesOrderNotes] =
		useState<FormattedSalesOrderNotes | null>(null);
	const [selectedBundles, setSelectedBundles] = useState<{
		[key: string]: any;
	}>([]);
	const [selectedSingleItems, setSelectedSingleItems] = useState<{
		[key: string]: any;
	}>([]);

	const getFieldsByHostAndPrivilege = () => {
		const fieldMapping: Record<
			string,
			{
				admin?: any;
				superuser?: any;
				manager?: any;
				globalAdmin?: any;
				itShopper?: any;
				buyer?: any;
			}
		> = {
			COMPASS: {
				admin: ADMINFIELDS_COMPASS,
				superuser: SUPERUSERFIELDS_COMPASS,
				manager: MANAGERFIELDS_COMPASS,
			},
			COMPASSCANADA: {
				admin: ADMINFIELDS_COMPASSCANADA,
				superuser: SUPERUSERFIELDS_COMPASSCANADA,
				manager: MANAGERFIELDS_COMPASSCANADA,
			},
			QUAD: {
				globalAdmin: ADMINFIELDS_QUAD,
				itShopper: ITSHOPPERFIELDS_QUAD,
				buyer: BUYERFIELDS_QUAD,
			},
		};

		const hostFields = fieldMapping[hostName];

		// Type guard to ensure user exists and user.role is a valid key
		if (!user) {
			return undefined;
		}

		const validRoles = [
			"admin",
			"superuser",
			"manager",
			"globalAdmin",
			"itShopper",
			"buyer",
		] as const;
		const role = user.role as string;

		if (hostFields && validRoles.includes(role as any)) {
			return hostFields[role as keyof typeof hostFields];
		}

		return undefined;
	};

	const fieldsFromCall = getFieldsByHostAndPrivilege();

	//// SETTINGS FROM FIELDS ////
	const isEnterPoNumberButtonVisible =
		fieldsFromCall?.modalHeader?.enterPoNumberButton?.isVisible;
	const poModalInfoSuccessToaster =
		fieldsFromCall?.enterPoNumberModal?.successToasterMessage?.label;

	const poModalInfoErrorToaster =
		fieldsFromCall?.enterPoNumberModal?.errorToasterMessage?.label;

	const { data: approvalDecisions, isLoading: isApprovalDecisionsLoading } =
		useQuery({
			queryKey: [
				"currentApprovalStage",
				activeUuid,
				user.uuid,
				refetchData,
			],
			queryFn: () =>
				getCurrentApprovalStage(activeUuid as string, user.uuid),
			enabled: !!activeUuid && !!user.uuid,
			refetchOnMount: "always",
			staleTime: 0,
		});

	const { data: orderItemsResponse, isLoading: isOrderItemsResponseLoading } =
		useQuery({
			queryKey: [
				"orderItemsResponse",
				activeUuid,
				user.uuid,
				fieldsFromCall,
				refetchData,
			],
			queryFn: () =>
				fetchOrderItems(
					activeUuid as string,
					fieldsFromCall?.fetchOrderItems,
				),
			enabled: !!activeUuid && !!user.uuid && !!fieldsFromCall,
			refetchOnMount: "always",
			staleTime: 0,
		});

	const addPurchaseOrderInputAndCreditCardInfoMutation = useMutation({
		mutationFn: ({
			salesOrderUuid,
			poNumber,
			customerUuid,
			dateOrder,
			purchaseOrderUuid,
			c_requiresCreditCard,
		}: {
			salesOrderUuid: string;
			poNumber: string;
			customerUuid: string;
			dateOrder: string;
			purchaseOrderUuid: string | null;
			c_requiresCreditCard: boolean;
		}) =>
			addPurchaseOrderInputAndCreditCardInfo(
				salesOrderUuid,
				poNumber,
				customerUuid,
				dateOrder,
				purchaseOrderUuid,
				c_requiresCreditCard,
			),
		onError: (error) => {
			addToaster({
				isOpen: true,
				message: `${poModalInfoErrorToaster}`,
				status: "error",
			});
		},
		onSuccess: () => {
			addToaster({
				isOpen: true,
				message: `${poModalInfoSuccessToaster}`,
				status: "success",
			});
			queryClient.invalidateQueries({
				queryKey: ["currentApprovalStage", activeUuid, user.uuid],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"purchaseOrderDetailsResponse",
					activeUuid,
					user.uuid,
				],
			});
		},
	});

	const {
		data: orderDetailsResponse,
		isLoading: isOrderDetailsResponseLoading,
	} = useQuery({
		queryKey: ["orderDetailsResponse", activeUuid, user.uuid, refetchData],
		queryFn: () =>
			fetchOrdersDetails(
				activeUuid as string,
				fieldsFromCall?.fetchOrdersDetails,
			),
		enabled: !!activeUuid && !!user.uuid && !!fieldsFromCall,
		refetchOnMount: "always",
		staleTime: 0,
	});

	const {
		data: purchaseOrderDetailsResponse,
		isLoading: isPurchaseOrderDetailsResponseLoading,
	} = useQuery({
		queryKey: ["purchaseOrderDetailsResponse", activeUuid, user.uuid],
		queryFn: () => fetchPurchaseOrderDetails(activeUuid as string),
		enabled: !!activeUuid && !!user.uuid && !!isEnterPoNumberButtonVisible,
		refetchOnMount: "always",
		staleTime: 0,
	});

	// Process initial data and set all states
	useEffect(() => {
		if (orderDetailsResponse && orderItemsResponse && fieldsFromCall) {
			const hasEditInCommercePermissions =
				pageMeta?.acl["sales-orders"]["navigation-edit-on-commerce"];
			const hasApprovalPermissions =
				pageMeta?.acl["sales-orders"]["navigation-approvals"];

			const salesOrderNotes = formatSalesOrderNotes(
				orderDetailsResponse?.salesOrderNotes,
			);

			setOrderDetails(orderDetailsResponse);
			setSalesOrderItems(orderItemsResponse);
			setAclPermissions({
				hasEditInCommercePermissions: hasEditInCommercePermissions,
				hasApprovalsPermissions: hasApprovalPermissions,
			});
			setSalesOrderNotes(salesOrderNotes);
			setOrderAddressDetails(orderDetailsResponse?.shipToAddress);

			// Mark initial processing as complete
			setIsInitialDataProcessed(true);
		}
	}, [orderDetailsResponse, fieldsFromCall, orderItemsResponse, pageMeta]);

	// Format bundles and items
	useEffect(() => {
		if (
			orderItemsResponse &&
			Array.isArray(orderItemsResponse) &&
			orderDetailsResponse &&
			fieldsFromCall?.settings.hasBundles
		) {
			setIsDataFormattingLoading(true);

			const fetchBundles = async () => {
				const { standaloneItems, bundles } =
					formatOrderItems(orderItemsResponse);

				setSelectedSingleItems(standaloneItems);

				const orderUsesParentSalesOrderId = bundles?.some(
					(bundleItem) => bundleItem.parentSalesOrderItem != null,
				);

				// If the order uses parentSalesOrderItem (compass USA orders after 2.0 launch)
				// OR, if the order is from a host that doesn't have the old bundle formatting (compass canada and quad), then use the pairing and fetching logic to ensure we get all bundle items and data
				if (
					orderUsesParentSalesOrderId ||
					!fieldsFromCall?.settings.hasOldBundleFormatting
				) {
					const checkPairedBundles =
						pairSalesOrderBundlesFromApi(bundles);
					const formattedBundles =
						await getChildBundleDataFromApi(checkPairedBundles);
					const formattedBundlesArray = formattedBundles.map(
						(bundle) =>
							formatSalesOrderBundles(
								bundle,
								orderDetailsResponse.salesOrderItems,
							),
					);
					setSelectedBundles(formattedBundlesArray);

					// TODO: for a different ticket, I don't think we need this anymore. it's only applicable to orders before september that are in PA or PIA
					// separate ticket to investigate if we can remove this logic and the old bundle formatting flag from fields
				} else {
					const primaryItems = getPrimaryItems(bundles);

					const bundleWithPrimaryItemAndBundleItems =
						getBundleWithPrimaryItemAndBundleItems(
							primaryItems,
							bundles,
						);

					const formattedBundlesArray =
						bundleWithPrimaryItemAndBundleItems.map((bundle) =>
							formatSalesOrderBundlesPreLaunchCommerce(
								bundle,
								orderDetailsResponse.salesOrderItems,
							),
						);

					setSelectedBundles(formattedBundlesArray);
				}

				setSelectedSingleItems(standaloneItems);
				setIsDataFormattingLoading(false); // Mark as complete after all state is set
			};

			fetchBundles();
		}

		if (
			orderItemsResponse &&
			Array.isArray(orderItemsResponse) &&
			orderDetailsResponse &&
			!fieldsFromCall?.settings.hasBundles
		) {
			setIsDataFormattingLoading(true);
			const { standaloneItems } = formatOrderItems(orderItemsResponse);
			setSelectedSingleItems(standaloneItems);
			setSelectedBundles([]);
			setIsDataFormattingLoading(false);
		}
	}, [orderDetailsResponse, orderItemsResponse]);

	// Show loading until ALL states are set (both React Query AND local state processing)
	const showLoadingState =
		!fieldsFromCall ||
		isOrderDetailsResponseLoading ||
		isOrderItemsResponseLoading ||
		isApprovalDecisionsLoading ||
		!isInitialDataProcessed ||
		isDataFormattingLoading ||
		(isEnterPoNumberButtonVisible &&
			isPurchaseOrderDetailsResponseLoading) ||
		!salesOrderItems;

	return {
		orderDetails,
		salesOrderItems,
		orderAddressDetails,
		fields: fieldsFromCall,
		setRefetchData,
		refetchData,
		aclPermissions,
		salesOrderNotes,
		selectedSingleItems,
		selectedBundles,
		showErrorMessage,
		approvalDecisions: approvalDecisions ?? ({} as ApprovalDecision),
		showLoadingState,
		activeUuid,
		addPurchaseOrderInputAndCreditCardInfoMutation,
		purchaseOrderDetails: purchaseOrderDetailsResponse?.[0] ?? {},
	};
};
